<template>
  <div class="fund-industry-detail page-shell" :class="{ 'is-mobile': isMobile }">
    <div class="toolbar">
      <el-button size="small" icon="el-icon-arrow-left" @click="goBack">返回</el-button>
      <span class="toolbar-label">报告期</span>
      <el-select
        v-model="selectedPeriod"
        size="small"
        placeholder="选择季度"
        :disabled="loadingPeriods || !periods.length"
        style="width: 140px"
        @change="onPeriodChange"
      >
        <el-option
          v-for="p in periods"
          :key="p"
          :label="formatPeriodLabel(p)"
          :value="p"
        />
      </el-select>
      <span v-if="industryName" class="toolbar-meta">证监会行业：{{ industryName }}</span>
      <span v-if="reportDate" class="toolbar-meta">截止日期：{{ reportDate }}</span>
      <span v-if="list.length" class="toolbar-meta">共 {{ list.length }} 只</span>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      class="error-alert"
    />

    <div ref="tableShell" class="table-shell" v-loading="loading" element-loading-text="加载中...">
      <el-table
        ref="detailTable"
        :data="list"
        size="small"
        :height="tableHeight"
        :row-style="{ height: '32px', lineHeight: '32px' }"
        :cell-style="{ padding: '2px 0' }"
        empty-text="暂无该行业基金明细"
        style="width: 100%"
      >
        <el-table-column
          prop="fundCode"
          label="基金代码"
          width="100"
          align="center"
          sortable
          :sort-method="sortFundCode"
        >
          <template slot-scope="{ row }">
            <a
              class="ext-link"
              :href="fundHref(row.fundCode)"
              target="_blank"
              rel="noopener noreferrer"
            >{{ row.fundCode }}</a>
          </template>
        </el-table-column>
        <el-table-column
          prop="fundName"
          label="基金名称"
          min-width="220"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <a
              class="ext-link"
              :href="fundHref(row.fundCode)"
              target="_blank"
              rel="noopener noreferrer"
            >{{ row.fundName }}</a>
          </template>
        </el-table-column>
        <el-table-column
          prop="marketValue"
          label="持仓市值(万元)"
          min-width="130"
          align="right"
          sortable
          :sort-method="sortMarketValue"
        >
          <template slot-scope="{ row }">
            {{ formatMarketValue(row.marketValue) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="navPct"
          label="占净值比"
          width="110"
          align="right"
          sortable
          :sort-method="sortNavPct"
        >
          <template slot-scope="{ row }">
            {{ formatNavPct(row.navPct) }}
          </template>
        </el-table-column>
        <el-table-column label="相关链接" min-width="180" align="center">
          <template slot-scope="{ row }">
            <a
              class="ext-link link-gap"
              :href="industryAllocHref(row.fundCode)"
              target="_blank"
              rel="noopener noreferrer"
            >行业配置</a>
            <a
              class="ext-link"
              :href="assetAllocHref(row.fundCode)"
              target="_blank"
              rel="noopener noreferrer"
            >资产配置</a>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import {
  fetchFundIndustryPeriods,
  fetchFundIndustryFundDetails
} from '../utils/fundIndustryApi.js'

const STOCKSTAR_ORIGIN = 'https://fund.stockstar.com'

function formatPeriodLabel(period) {
  const m = String(period || '').match(/^(\d{4})Q([1-4])$/)
  if (!m) return period
  return `${m[1]}年${m[2]}季度`
}

export default {
  name: 'FundIndustryDetail',
  props: {
    visible: { type: Boolean, default: false },
    isMobile: { type: Boolean, default: false },
    period: { type: String, default: '' },
    code: { type: String, default: '' }
  },
  data() {
    return {
      periods: [],
      selectedPeriod: '',
      industryName: '',
      reportDate: '',
      list: [],
      loading: false,
      loadingPeriods: false,
      error: '',
      tableHeight: 560,
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.updateTableHeight()
        this.ensureLoaded()
      }
    },
    period() {
      if (this.visible) this.syncFromProps()
    },
    code() {
      if (this.visible) this.syncFromProps()
    },
    error() {
      this.updateTableHeight()
    },
    industryName() {
      this.updateTableHeight()
    },
    reportDate() {
      this.updateTableHeight()
    },
  },
  mounted() {
    this.updateTableHeight()
    window.addEventListener('resize', this.updateTableHeight, { passive: true })
    if (this.visible) this.ensureLoaded()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTableHeight)
  },
  methods: {
    formatPeriodLabel,
    updateTableHeight() {
      this.$nextTick(() => {
        const shell = this.$refs.tableShell
        if (!shell) return
        const top = shell.getBoundingClientRect().top
        const height = Math.max(240, Math.floor(window.innerHeight - top - 20))
        if (height !== this.tableHeight) this.tableHeight = height
        this.$nextTick(() => {
          this.$refs.detailTable?.doLayout?.()
        })
      })
    },
    async ensureLoaded() {
      await this.loadPeriods()
      this.syncFromProps()
    },
    syncFromProps() {
      const code = String(this.code || '').trim().toUpperCase()
      if (!code) {
        this.error = '缺少行业代码'
        this.list = []
        return
      }
      let period = String(this.period || '').trim()
      if (!period && this.periods.length) {
        period = this.periods[0]
      }
      if (period && period !== this.selectedPeriod) {
        this.selectedPeriod = period
      }
      if (this.selectedPeriod) {
        this.loadDetails()
      }
    },
    async loadPeriods() {
      if (this.periods.length) return
      this.loadingPeriods = true
      try {
        const periods = await fetchFundIndustryPeriods()
        this.periods = Array.isArray(periods)
          ? periods.map((p) => (typeof p === 'string' ? p : p.period)).filter(Boolean)
          : []
      } catch (e) {
        this.error = e.message || '加载报告期失败'
      } finally {
        this.loadingPeriods = false
      }
    },
    async loadDetails() {
      const period = this.selectedPeriod
      const code = String(this.code || '').trim().toUpperCase()
      if (!period || !code) return
      this.loading = true
      this.error = ''
      try {
        const data = await fetchFundIndustryFundDetails(period, code)
        this.industryName = data?.industryName || code
        this.reportDate = data?.reportDate || ''
        this.list = Array.isArray(data?.list) ? data.list : []
      } catch (e) {
        this.list = []
        this.error = e.message || '加载基金明细失败'
      } finally {
        this.loading = false
        this.updateTableHeight()
      }
    },
    onPeriodChange() {
      const code = String(this.code || '').trim().toUpperCase()
      const q = new URLSearchParams({
        period: this.selectedPeriod,
        code
      })
      const hash = `#/fund-industry-detail?${q}`
      if (window.location.hash !== hash) {
        window.history.replaceState(null, '', hash)
      }
      this.loadDetails()
    },
    goBack() {
      window.location.hash = '#/fund-industry'
    },
    fundHref(code) {
      return `${STOCKSTAR_ORIGIN}/funds/${code}.shtml`
    },
    industryAllocHref(code) {
      return `${STOCKSTAR_ORIGIN}/funds/f10/fundhy_${code}.html`
    },
    assetAllocHref(code) {
      return `${STOCKSTAR_ORIGIN}/funds/f10/fundzcpz_${code}.html`
    },
    formatMarketValue(v) {
      if (v == null || v === '') return '--'
      const n = Number(v)
      if (!Number.isFinite(n)) return '--'
      return n.toFixed(2)
    },
    formatNavPct(v) {
      if (v == null || v === '') return '--'
      const n = Number(v)
      if (!Number.isFinite(n)) return '--'
      return `${n.toFixed(2)}%`
    },
    sortFundCode(a, b) {
      return String(a.fundCode || '').localeCompare(String(b.fundCode || ''), 'zh')
    },
    sortMarketValue(a, b) {
      const av = a.marketValue == null ? -Infinity : Number(a.marketValue)
      const bv = b.marketValue == null ? -Infinity : Number(b.marketValue)
      return av - bv
    },
    sortNavPct(a, b) {
      const av = a.navPct == null ? -Infinity : Number(a.navPct)
      const bv = b.navPct == null ? -Infinity : Number(b.navPct)
      return av - bv
    }
  }
}
</script>

<style scoped>
.fund-industry-detail {
  width: 100%;
  min-width: 0;
}

.fund-industry-detail.is-mobile {
  padding: 0;
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 16px;
  text-align: left;
}

.toolbar-label {
  font-size: 14px;
  color: #606266;
}

.toolbar-meta {
  margin-left: 4px;
  font-size: 13px;
  color: #909399;
}

.error-alert {
  margin-bottom: 12px;
}

.table-shell {
  min-height: 0;
}

.ext-link {
  color: #409eff;
  text-decoration: none;
}

.ext-link:hover {
  text-decoration: underline;
}

.link-gap {
  margin-right: 10px;
}
</style>
